import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTableCore from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  Accordion,
  Button,
  Checkbox,
  Icon,
  Popup,
  Menu,
  Ref,
} from 'semantic-ui-react';
import _ from 'lodash';

import {
  Config,
  DefaultTableState,
  ReactTablePropNameEnum,
  DefaultColumnKeysSaved,
} from 'Common/Constants/table';
import { setTableStates } from 'Common/UserSettings/state/actions';
import { getTableStates } from 'Common/UserSettings/helpers/helper';
import { toastError, errorToast, toastSuccess } from 'Common/ToastHelper';
import { GridPreferenceEnum } from 'Common/FieldEnum';
import {
  getColumnOrderSchema,
  saveColumnOrderSchema,
  clearGridPreference,
  clearGridPreferenceSubmitError,
} from 'State/gridPreference/actions';

import { ControlledPagination } from './ControlledPagination';
import { PlaceholderInput } from './PlaceholderInput';
// https://github.com/GuillaumeJasmin/react-table-hoc-fixed-columns
const ReactTableFixedColumns = withFixedColumns(ReactTableCore);

class ReactTableContainer extends Component {
  static propTypes = {
    data: PropTypes.oneOfType([
      PropTypes.array.isRequired,
      PropTypes.object.isRequired,
    ]),
    columns: PropTypes.array.isRequired,
    // locally saved state
    saveState: PropTypes.shape({
      tableName: PropTypes.string.isRequired,
      filtered: PropTypes.bool,
      sorted: PropTypes.bool,
      pageSize: PropTypes.bool,
      page: PropTypes.bool,
    }),
    /** hasCogs states that you're passing a cog column as a column */
    menu: PropTypes.oneOf(['hasCogs', true, false]),
    // Determines whether to fix cog to the right of the table
    fixCog: PropTypes.bool,
    // Determines whether the column visibility accordion is shown
    showHideColumn: PropTypes.bool,
    // Determines whether the columns should be reorderable
    draggableColumns: PropTypes.bool,
    getTheadThProps: PropTypes.func,
    saveColumnOrderSchema: PropTypes.func.isRequired,
    /** This MUST be React.createRef */
    reactTableRef: PropTypes.object,
    controlledPaging: PropTypes.bool,
    // Callback for when the page has changed
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    resetPageOnDataChange: PropTypes.bool,

    // hasSelectAllColumn states that you're passing an additional column that is meant for selecting the rows
    hasSelectAllColumn: PropTypes.bool,
    /** table can contain expander with checkbox.Example manage process order page **/
    hasExpanderColumn: PropTypes.bool,
    /**
     * This is used as the key for saving the grid's preferences in the database
     * new gridName should be placed in GridEnum
     */
    gridName: PropTypes.string,
    onRef: PropTypes.func,

    onFilteredChange: PropTypes.func,
    onSortedChange: PropTypes.func,

    // Determines whether to show the table options menu
    showMenu: PropTypes.bool,

    // Determines whether default grid should filter even in manual and controlled paging mode
    enableControlledPagingSortFilter: PropTypes.bool,
    minRows: PropTypes.number,
    showFiltersPlaceholderText: PropTypes.bool,
  };

  static defaultProps = {
    menu: true,
    fixCog: true,
    controlledPaging: false,
    onPageChange: (page) => {},
    onPageSizeChange: (pageSize, page) => {},
    onSortedChange: (sorted) => {},
    onFilteredChange: (filtered) => {},
    resetPageOnDataChange: true,
    hasSelectAllColumn: false,
    hasExpanderColumn: false,
    showMenu: true,
    enableControlledPagingSortFilter: false,
    minRows: 5,
    showFiltersPlaceholder: false,
  };

  popupId = 'react-table-popup';

  wrapperRef = React.createRef();
  innerTableRef = React.createRef();
  dragSource = null;
  dragDestination = null;
  state = {
    columns: this.props.columns,
    tableState: DefaultTableState,
    dropped: false,
    open: false,
    popupStyle: {},
    accordionIndex: -1,
    options: {
      lockedFirstColumn: false,
    },
    hasSavedPreference: false, // if the user has saved preferences
  };

  /** Keep track of scroll position */
  columnVisilityScrollY = 0;
  columnVisibilityRef = null;
  /** Need to use functional ref because the Ref component only takes in the functional ref handler for this version of Semantic React (V0.79) */
  handleRef = (node) => {
    this.columnVisibilityRef = node;
  };

  constructor(props) {
    super(props);

    const {
      saveState,
      allTableStates,
      columns,
      defaultSorted,
      defaultFiltered,
      defaultPageSize,
    } = props;

    const { tableState } = this.state;

    if (!isNaN(defaultPageSize)) {
      this.state['tableState'] = {
        ...tableState,
        [ReactTablePropNameEnum.pageSize]: defaultPageSize,
      };
    }

    if (Array.isArray(defaultSorted) && defaultSorted.length > 0) {
      this.state['tableState'] = {
        ...tableState,
        [ReactTablePropNameEnum.sorted]: [...defaultSorted],
      };
    }

    if (Array.isArray(defaultFiltered) && defaultFiltered.length > 0) {
      this.state['tableState'] = {
        ...tableState,
        [ReactTablePropNameEnum.filtered]: [...defaultFiltered],
      };
    }

    this.state['columns'] = columns;

    // Check if the user has passed a tableName thus enabling saving to the store.
    this.state['shouldSaveToStore'] = _.has(saveState, 'tableName');
    // Restore the tables state.
    if (saveState && _.has(allTableStates, [saveState.tableName])) {
      const savedTableState = _.get(allTableStates, [saveState.tableName]);

      const localColumnState = this.getLocalColumnState();

      this.state['tableState'] = {
        ...tableState,
        ...savedTableState,
      };

      if (localColumnState) {
        this.state['columns'] = localColumnState;
      }

      this.state.options['lockedFirstColumn'] = _.get(
        savedTableState,
        ['lockedColumns', '0'],
        false
      );
    }
  }

  componentDidMount() {
    const {
      getColumnOrderSchema,
      gridName,
      onRef,
      onFilteredChange,
      onSortedChange,
    } = this.props;

    // issue raised to clear the filter when user click on 'Clear filter' button which is outside of the grid.Example onHand report,transaction summary etc.
    // while ref is giving null for wrapper.
    onRef && onRef(this);
    gridName && getColumnOrderSchema(gridName);
    if (this.innerTableRef.current) {
      const instance = this.innerTableRef.current;
      const { state } = instance;
      onFilteredChange(state.filtered);
      onSortedChange(state.sorted);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      columns,
      resetPageOnDataChange,
      onResetPageOnDataChange,
      gridPreferenceData,
      gridName,
      clearGridPreferenceSubmitError,
      menu,
      hasSelectAllColumn,
      hasExpanderColumn,
      onPageChange,
      onFilteredChange,
      onSortedChange,
    } = this.props;

    const { tableState } = this.state;

    if (prevProps.columns !== columns) {
      this.setState({ columns: columns });
    }

    const filtersHaveChanged =
      prevState.tableState[ReactTablePropNameEnum.filtered] !==
      tableState[ReactTablePropNameEnum.filtered];

    // The react table isn't currently handling changing the page when the data changes or is filtered.
    // It is possible for the dataset to be filtered to only a couple items, but the table will remain on page 6 where no items are shown.
    // need to reset the page on outter filter change such status change(All,Open and close) so introduced resetPageOnDataChange.
    if (
      filtersHaveChanged ||
      (prevProps.data !== this.props.data && resetPageOnDataChange)
    ) {
      this.handleTableStateChange({
        [ReactTablePropNameEnum.page]: DefaultTableState.page,
      });
      onResetPageOnDataChange && onResetPageOnDataChange();
    }

    // previously had filters, now don't have any filters, should reset to default page
    const filteredLengthPath = [
      'tableState',
      [ReactTablePropNameEnum.filtered],
      'length',
    ];
    if (
      _.get(prevState, filteredLengthPath, 0) > 0 &&
      _.get(this.state, filteredLengthPath, 0) < 1
    ) {
      onPageChange(DefaultTableState.page);
    }

    if (
      errorToast(this.props, prevProps, 'preferenceJsonError') ||
      errorToast(this.props, prevProps, 'gridPreferenceSubmitError')
    ) {
      clearGridPreferenceSubmitError();
    }

    try {
      let hasSavedPreference = false;
      if (
        gridPreferenceData &&
        gridName &&
        gridPreferenceData.hasOwnProperty(gridName) &&
        !prevProps.gridPreferenceData.hasOwnProperty(gridName) &&
        gridPreferenceData[gridName].PreferenceJson &&
        JSON.parse(gridPreferenceData[gridName].PreferenceJson).length
      ) {
        hasSavedPreference = true;
        let columnsCopy = _.cloneDeep(this.getColumns());

        let menuColumn = null;
        if (menu === 'hasCogs') {
          menuColumn = columnsCopy.pop();
        }

        const preferenceData = JSON.parse(
          gridPreferenceData[gridName].PreferenceJson
        );

        let ordered = [];

        const options = {
          ...this.state.options,
        };

        const firstColumns = [];

        if (hasSelectAllColumn || hasExpanderColumn) {
          firstColumns.push(columnsCopy.shift());
        }

        if (hasSelectAllColumn && hasExpanderColumn) {
          firstColumns.push(columnsCopy.shift());
        }

        preferenceData.forEach((c, index) => {
          const stateCol = _.remove(
            columnsCopy,
            (sc) =>
              (!_.isNil(sc.accessor) && sc.accessor === c.accessor) ||
              (!_.isNil(sc.id) && sc.id === c.id) ||
              (!_.isNil(sc.Header) && sc.Header === c.Header)
          );
          const obj = stateCol.length ? { ...stateCol[0], ...c } : c;

          if (index === 0) {
            options.lockedFirstColumn = !_.isNil(c.locked) ? c.locked : false;
          }
          ordered.push(obj);
        });

        const filtered = preferenceData
          .filter((c) => Boolean(c[GridPreferenceEnum.filter]))
          .map((c) => ({
            id: c[GridPreferenceEnum.id] || c[GridPreferenceEnum.accessor],
            value: c[GridPreferenceEnum.filter],
          }));

        const sorted = preferenceData
          .filter((c) => !_.isNil(c[GridPreferenceEnum.sort]))
          .map((c) => ({
            id: c[GridPreferenceEnum.id] || c[GridPreferenceEnum.accessor],
            desc: c[GridPreferenceEnum.sort],
          }));

        const columns = [...firstColumns, ...ordered, ...columnsCopy];

        if (menuColumn) {
          columns.push(menuColumn);
        }

        const state = {
          columns,
          options,
          tableState: { ...tableState, filtered, sorted },
          hasSavedPreference,
        };

        state.tableState.lockedColumns[0] = options.lockedFirstColumn;

        this.setState(state, () => {
          onFilteredChange(filtered);
          onSortedChange(sorted);
        });
      }
    } catch (err) {
      toastError(
        'An error occurred while fetching grid preference. Please try again.'
      );
    }
  }
  componentWillUnmount() {
    const { clearGridPreference, gridName, onRef } = this.props;
    onRef && onRef(undefined);
    gridName && clearGridPreference(gridName);
  }

  getLocalColumnState = () => {
    const {
      saveState,
      allTableStates,
      columns,
      hasSelectAllColumn,
      hasExpanderColumn,
    } = this.props;
    let ordered = null;
    // Restore the tables state.
    if (saveState && _.has(allTableStates, [saveState.tableName])) {
      ordered = [];

      const savedTableState = _.get(allTableStates, [saveState.tableName]);

      const savedStateColumns = (savedTableState.columns || []).filter(
        (c) => !_.isEmpty(c)
      );

      const cloneStateColumns = [...columns];

      const firstColumns = [];
      if (hasSelectAllColumn || hasExpanderColumn) {
        firstColumns.push(cloneStateColumns.shift());
      }

      if (hasSelectAllColumn && hasExpanderColumn) {
        firstColumns.push(cloneStateColumns.shift());
      }

      savedStateColumns.forEach((c) => {
        const stateCol = _.remove(
          cloneStateColumns,
          (sc) =>
            (!_.isNil(sc.accessor) && sc.accessor === c.accessor) ||
            (!_.isNil(sc.id) && sc.id === c.id) ||
            (!_.isNil(sc.Header) && sc.Header === c.Header)
        );
        const obj = stateCol.length ? { ...stateCol[0], ...c } : c;
        //ordered array also contain firstColumns data.Removing those duplicate column.
        const index = _.findIndex(
          firstColumns,
          (col) =>
            (!_.isNil(col.accessor) && col.accessor === obj.accessor) ||
            (!_.isNil(col.id) && col.id === obj.id) ||
            (!_.isNil(col.Header) && col.Header === obj.Header)
        );
        index === -1 && ordered.push(obj);
      });

      ordered = [...firstColumns, ...ordered, ...cloneStateColumns];
    }
    return ordered;
  };

  // Update the temp state of the table.
  handleTableStateChange = (changed) => {
    const { onPageChange } = this.props;
    let { filtered } = changed;

    // If there are filters, reset the page to default page, 0, the first page
    if (filtered && filtered.length) {
      changed.page = DefaultTableState.page;
      onPageChange(DefaultTableState.page);
    }
    const tableState = {
      ...this.state.tableState,
      ...changed,
    };
    this.setState({ tableState }, this.tableStateChanged);
  };

  // Save the table state to the store.
  tableStateChanged = _.debounce(
    () => {
      const { tableState } = this.state;
      const {
        setTableStates,
        allTableStates,
        saveState,
        onFetchData,
        reactTableRef,
      } = this.props;

      //fetch data for pagination regardless of whether state is saved
      onFetchData && onFetchData(tableState, reactTableRef);

      // Return if the state shouldn't be saved.
      if (!this.state.shouldSaveToStore) {
        return;
      }

      // Save only items which are supposed to be saved in store.
      let stateToStore = {};
      for (const propName in ReactTablePropNameEnum) {
        if (_.get(saveState, propName, true)) {
          stateToStore[propName] = tableState[propName];
        }
      }
      setTableStates({
        ...allTableStates,
        [saveState.tableName]: stateToStore,
      });
    },
    500,
    { leading: false }
  );

  // Clear the table state.
  clearTableState = () => {
    const {
      columns,
      onFilteredChange,
      onSortedChange,
      defaultPageSize,
    } = this.props;

    const defaultTableState = { ...DefaultTableState };
    if (defaultPageSize) {
      defaultTableState.pageSize = defaultPageSize;
    }

    this.setState(
      {
        tableState: defaultTableState,
        columns,
        open: false,
        options: {
          lockedFirstColumn: false,
        },
        lockedColumns: {},
      },
      () => {
        this.handleTableStateChange({
          [ReactTablePropNameEnum.sorted]: [],
          [ReactTablePropNameEnum.filtered]: [],
          [ReactTablePropNameEnum.lockedColumns]: {},
        });
        this.tableStateChanged();
        this.handleColumnsChange(columns);
        onFilteredChange([]);
        onSortedChange([]);
      }
    );
  };

  getColumns = () => {
    const { lockedColumns } = this.state.tableState;
    const {
      menu,
      fixCog,
      hasSelectAllColumn,
      hasExpanderColumn,
      showMenu,
      showFiltersPlaceholderText,
    } = this.props;
    const columns = [...this.state.columns];

    if (!menu) {
      return columns;
    }

    let menuColumn = {
      resizable: false,
      sortable: false,
      filterable: false,
      width: 20,
      Header: showMenu ? this.getMenu : <div />,
    };

    if (menu === 'hasCogs') {
      const cogsColumn = columns.pop();
      menuColumn = {
        ...menuColumn,
        ...cogsColumn,
        key: 'menu',
        DO_NOT_SAVE_ORDER: true,
      };
    }

    const allColumns = [...columns, menuColumn];

    let numLeftColumnsToFix = 0;
    if (hasSelectAllColumn) {
      numLeftColumnsToFix += 1;
    }
    if (hasExpanderColumn) {
      numLeftColumnsToFix += 1;
    }

    for (let i = 0; i < numLeftColumnsToFix; i += 1) {
      allColumns[i] = {
        ..._.cloneDeep(allColumns[i]),
        DO_NOT_SAVE_ORDER: true,
      };
    }

    let lockedIterator = 0;
    /** Iterate through all columns and apply the fixed key */
    return allColumns
      .filter((c) => {
        return Boolean(c.id) || Boolean(c.Header) || Boolean(c.accessor);
      })
      .map((c) => {
        if (c.key === 'menu' && fixCog) {
          c.fixed = 'right';
          return c;
        }

        if (!c.show) {
          return c;
        }

        if (
          lockedColumns[lockedIterator] ||
          (hasSelectAllColumn && lockedIterator === 1) ||
          (hasExpanderColumn && lockedIterator === 2)
        ) {
          c.fixed = 'left';
          lockedIterator += 1;
        } else {
          delete c.fixed;
        }
        return c;
      })
      .map((c) => {
        // if showPlaceholderText prop is set and not already using a custom Filter, use the placeholder input
        if (showFiltersPlaceholderText && !c.Filter) {
          c.Filter = PlaceholderInput;
        }
        return c;
      });
  };

  draggableHeader = (state, rowInfo, column) => {
    let columns = [...this.state.columns];
    const getTheadThProps = this.props.getTheadThProps
      ? this.props.getTheadThProps(state, rowInfo, column)
      : {};

    if (column && column.fixed) {
      return getTheadThProps;
    } else if (state.currentlyResizing) {
      const index = columns.findIndex((column) => {
        return column.accessor === state.currentlyResizing.id;
      });
      if (index && index > -1) {
        columns[index].columnSize = state.currentlyResizing.parentWidth;
      }
      return getTheadThProps;
    } else {
      return {
        draggable: true,
        onDragStart: (e) => {
          const rect = e.target.getBoundingClientRect();
          const { clientX, clientY } = e;
          if (clientX > rect.left + 18 && clientY < rect.right - 18) {
            this.dragSource = _.findIndex(state.columns, function (col) {
              return col.Header === column.Header;
            });
          } else {
            e.preventDefault();
          }
          /** reset locked columns to remove the fixed column UI */
          this.setState({
            tableState: { ...this.state.tableState, lockedColumns: {} },
          });
        },
        //the drop header
        onDragOver: (e) => e.preventDefault(),
        onDrop: (e) => {
          e.preventDefault();
          this.dragDestination = _.findIndex(state.columns, function (col) {
            return col.Header === column.Header;
          });
          //reorder columns array
          const columnsCopy = _.cloneDeep(state.columns);

          const columnToMove = columnsCopy[this.dragSource];
          columnsCopy.splice(this.dragSource, 1);
          columnsCopy.splice(this.dragDestination, 0, columnToMove);

          const updatedState = {
            ...this.state,
            columns: columnsCopy,
            dropped: true,
          };

          /** Apply the locked columns on drop */
          if (this.state.options.lockedFirstColumn) {
            updatedState.tableState.lockedColumns[0] = true;
          }

          this.setState(updatedState, () => {
            this.handleColumnsChange(columnsCopy);
          });
          this.dragSource = null;
          this.dragDestination = null;
        },
        ...getTheadThProps,
      };
    }
  };

  handleSavePreference = () => {
    const preferenceJson = [];
    const { gridPreferenceData, gridName, saveColumnOrderSchema } = this.props;
    const { options, tableState } = this.state;
    const allColumns = this.getColumns();

    const filtered = _.get(tableState, 'filtered', []).filter(
      (x) => !_.isNil(x.id)
    );
    const sorted = _.get(tableState, 'sorted', []).filter(
      (x) => !_.isNil(x.id)
    );

    allColumns
      .filter((c) => !c.DO_NOT_SAVE_ORDER)
      .forEach((column, index) => {
        if (
          column[GridPreferenceEnum.accessor] ||
          column[GridPreferenceEnum.id]
        ) {
          const obj = _.pick(column, [
            GridPreferenceEnum.accessor,
            GridPreferenceEnum.show,
            GridPreferenceEnum.id,
            GridPreferenceEnum.Header,
          ]);

          const columnFilter = filtered.find(
            (x) =>
              x.id === column[GridPreferenceEnum.accessor] ||
              x.id === column[GridPreferenceEnum.id]
          );

          const columnSort = sorted.find(
            (x) =>
              x.id === column[GridPreferenceEnum.accessor] ||
              x.id === column[GridPreferenceEnum.id]
          );

          if (columnFilter) {
            obj[GridPreferenceEnum.filter] = columnFilter.value;
          }

          if (columnSort) {
            obj[GridPreferenceEnum.sort] = columnSort.desc;
          }

          obj[GridPreferenceEnum.locked] = false;
          if (options.lockedFirstColumn && index === 0) {
            obj[GridPreferenceEnum.locked] = true;
          }
          preferenceJson.push(obj);
        }
      });

    const gridPreferenceId =
      gridPreferenceData &&
      gridPreferenceData[gridName] &&
      gridPreferenceData[gridName].Id;

    saveColumnOrderSchema(preferenceJson, gridPreferenceId, gridName).then(
      () => {
        toastSuccess('Column preference has been saved successfully');
      }
    );
  };

  handleColumnsChange = (columns) => {
    const formattedColumns = columns
      .filter((c) => !c.DO_NOT_SAVE_ORDER)
      .map((c) => _.pick(c, DefaultColumnKeysSaved))
      .filter((c) => !_.isEmpty(c));

    this.handleTableStateChange({
      [ReactTablePropNameEnum.columns]: formattedColumns,
    });
  };

  handleOpen = (event, data) => {
    this.setState({ open: true }, () => {
      setTimeout(() => {
        const popup = document.getElementById(this.popupId);
        if (popup) {
          this.setState({
            popupStyle: {
              position: 'absolute',
              transform: popup.style.transform,
            },
          });
        }
      }, 500);
    });
  };

  handleClose = () => {
    this.columnVisilityScroll = {
      scrollTop: 0,
      scrollLeft: 0,
    };
    this.setState({ open: false, popupStyle: {}, accordionIndex: -1 });
  };

  handleAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    const { accordionIndex } = this.state;
    const newIndex = accordionIndex === index ? -1 : index;

    this.setState({ accordionIndex: newIndex });
  };

  /**
   * Have to keep track of the scroll position because it's a new element that gets created on each render
   * then inserted into the column header, causing the scroll position to be lost if it's not actively  tracked
   */
  onAccordionScroll = (e) => {
    if (e && e.target) {
      this.columnVisilityScroll = {
        scrollTop: e.target.scrollTop,
        scrollLeft: e.target.scrollLeft,
      };
    }
  };

  getMenu = () => {
    const {
      columns,
      open,
      tableState,
      accordionIndex,
      options,
      popupStyle,
    } = this.state;

    const {
      showHideColumn,
      hasSelectAllColumn,
      gridName,
      onFilteredChange,
      onSortedChange,
      defaultPageSize,
    } = this.props;

    const defaultTableState = { ...DefaultTableState };
    if (defaultPageSize) {
      defaultTableState.pageSize = defaultPageSize;
    }

    const buttons = [
      {
        key: 1,
        text: 'Reset Table',
        icon: 'undo',
        handler: this.clearTableState,
        disabled:
          _.isEqual(tableState, defaultTableState) &&
          _.isEqual(columns, this.props.columns),
      },
      {
        key: 2,
        text: 'Clear Sorting',
        icon: 'sort',
        handler: () => {
          this.handleTableStateChange({
            [ReactTablePropNameEnum.sorted]: [],
          });
          this.setState({ open: !open });
          onSortedChange([]);
        },
        disabled: _.isEqual(
          tableState[ReactTablePropNameEnum.sorted],
          DefaultTableState.sorted
        ),
      },
      {
        key: 3,
        text: 'Clear Filters',
        icon: 'filter',
        handler: () => {
          this.handleTableStateChange({
            [ReactTablePropNameEnum.filtered]: [],
          });
          this.setState({ open: !open });
          onFilteredChange([]);
        },
        disabled: _.isEqual(
          tableState[ReactTablePropNameEnum.filtered],
          DefaultTableState.filtered
        ),
      },
      {
        key: 4,
        text: 'Save Preference',
        handler: () => {
          this.handleSavePreference();
          this.setState({ open: !open });
        },
        icon: 'save',
        disabled:
          !gridName ||
          (_.isEqual(columns, this.props.columns) &&
            !this.props.showHideColumn),
      },
    ];

    const allChecked =
      _.filter(columns, { visibility: true }).length ===
      _.filter(columns, { show: true, visibility: true }).length;

    return (
      <Popup
        id={this.popupId}
        on={'click'}
        position={'left center'}
        style={{ zIndex: 10002, width: 100, ...popupStyle }}
        open={open}
        onClose={this.handleClose}
        onOpen={this.handleOpen}
        trigger={
          <Icon
            name={'ellipsis vertical'}
            className={'large pointer-cursor'}
            onClick={(event) => {
              event.preventDefault();
            }}
          />
        }
      >
        <Accordion
          style={{
            width: 250,
            textAlign: 'left',
          }}
          as={Menu}
          vertical
        >
          <Accordion.Title
            active={accordionIndex === 0}
            content="Options"
            index={0}
            onClick={this.handleAccordionClick}
          />
          <Accordion.Content active={accordionIndex === 0}>
            <Menu.Item
              style={{
                borderBottom: '1px solid lightgray',
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
              }}
            >
              <Checkbox
                label={'Lock First Column'}
                type={'checkbox'}
                checked={options.lockedFirstColumn}
                onClick={(e, data) => {
                  const state = this.state;
                  let lockedColumnsCopy = {
                    ...state.tableState.lockedColumns,
                  };

                  state.options.lockedFirstColumn = data.checked;

                  if (data.checked) {
                    lockedColumnsCopy[0] = true;
                  } else {
                    lockedColumnsCopy = {
                      ..._.omit(lockedColumnsCopy, ['0']),
                    };
                  }

                  state.tableState.lockedColumns = lockedColumnsCopy;

                  this.setState(state, () => {
                    this.handleTableStateChange({
                      [ReactTablePropNameEnum.lockedColumns]: lockedColumnsCopy,
                    });
                  });
                }}
              />
            </Menu.Item>
          </Accordion.Content>

          {showHideColumn && (
            <React.Fragment>
              <Accordion.Title
                active={accordionIndex === 1}
                content="Column Visibility"
                index={1}
                onClick={this.handleAccordionClick}
              />
              <Ref innerRef={this.handleRef}>
                <Accordion.Content
                  active={accordionIndex === 1}
                  className="grid-accordion-content"
                  onScroll={this.onAccordionScroll}
                >
                  <Menu.Item
                    style={{
                      borderBottom: '1px solid lightgray',
                    }}
                  >
                    <Checkbox
                      label="ALL"
                      type={'checkbox'}
                      checked={allChecked}
                      onClick={(e, data) => {
                        let stateColumnCopy = _.cloneDeep(columns);

                        stateColumnCopy = stateColumnCopy.map((c, index) => ({
                          ...c,
                          show:
                            // always show at least one column, last column is usually the cogs column
                            stateColumnCopy.length - 1 === index ||
                            (hasSelectAllColumn && index === 0) ||
                            !c.visibility
                              ? true
                              : !allChecked,
                        }));

                        this.setState(
                          {
                            columns: stateColumnCopy,
                          },
                          () => {
                            // on next frame set the scroll positions
                            requestAnimationFrame(() => {
                              if (
                                this.columnVisibilityRef &&
                                this.columnVisilityScroll
                              ) {
                                this.columnVisibilityRef.scrollLeft = this.columnVisilityScroll.scrollLeft;
                                this.columnVisibilityRef.scrollTop = this.columnVisilityScroll.scrollTop;
                              }
                            });
                            this.handleColumnsChange(stateColumnCopy);
                          }
                        );

                        e.preventDefault();
                      }}
                    />
                  </Menu.Item>

                  {columns.map((column, index) => {
                    if (column.visibility) {
                      let label =
                        typeof column.Header === 'function' ? (
                          column.visibilityName ? (
                            column.visibilityName
                          ) : (
                            <label>{column.Header()}</label>
                          )
                        ) : (
                          column.Header
                        );

                      return (
                        label && (
                          <Menu.Item key={index}>
                            <Checkbox
                              label={label}
                              type={'checkbox'}
                              checked={column.show || column.show === undefined}
                              onClick={(e, data) => {
                                let stateColumnCopy = _.cloneDeep(columns);
                                stateColumnCopy[index].show = data.checked;
                                this.setState(
                                  {
                                    columns: stateColumnCopy,
                                  },
                                  () => {
                                    // on next frame set the scroll positions
                                    requestAnimationFrame(() => {
                                      if (
                                        this.columnVisibilityRef &&
                                        this.columnVisilityScroll
                                      ) {
                                        this.columnVisibilityRef.scrollLeft = this.columnVisilityScroll.scrollLeft;
                                        this.columnVisibilityRef.scrollTop = this.columnVisilityScroll.scrollTop;
                                      }
                                    });
                                  }
                                );
                                this.handleColumnsChange(stateColumnCopy);
                              }}
                            />
                          </Menu.Item>
                        )
                      );
                    } else {
                      return null;
                    }
                  })}
                </Accordion.Content>
              </Ref>
            </React.Fragment>
          )}
        </Accordion>

        {buttons.map((option) => {
          return (
            !option.disabled && (
              <Button
                key={option.key}
                size="mini"
                onClick={option.handler}
                icon={option.icon}
                content={option.text}
              />
            )
          );
        })}
      </Popup>
    );
  };

  // https://github.com/tannerlinsley/react-table/issues/1040
  // when manual is true, disables all default sorting/filter
  // trigger sort/filter manually
  controlledPagingSortFilter = (data) => {
    if (!this.innerTableRef.current) {
      return [];
    }
    const instance = this.innerTableRef.current;
    const { state } = instance;
    const sortMethodsByColumnID = {};

    state.allDecoratedColumns
      .filter((col) => col.sortMethod)
      .forEach((col) => {
        sortMethodsByColumnID[col.id] = col.sortMethod;
      });

    const sortedData = instance.sortData(
      instance.filterData(
        data,
        state.filtered,
        state.defaultFilterMethod,
        state.allDecoratedColumns
      ),
      state.sorted,
      sortMethodsByColumnID
    );
    return sortedData;
  };

  render() {
    const {
      saveState,
      getTheadThProps,
      draggableColumns,
      reactTableRef,
      onFetchData,
      columns,
      controlledPaging,
      onSortedChange,
      onFilteredChange,
      onPageChange,
      onPageSizeChange,
      data,
      manual,
      enableManualSortFilter,
      ...rest
    } = this.props;

    const allColumns = this.getColumns();

    const tableData =
      manual && enableManualSortFilter
        ? this.controlledPagingSortFilter(data)
        : data;

    return (
      <ReactTableFixedColumns
        ref={this.wrapperRef}
        {...(controlledPaging && {
          PaginationComponent: ControlledPagination,
        })}
        {...Config}
        {...this.state.tableState}
        columns={allColumns}
        onFilteredChange={(filtered) => {
          // on search need to uncheck the selected items.
          this.handleTableStateChange({
            [ReactTablePropNameEnum.filtered]: filtered,
          });
          onFilteredChange(filtered);
        }}
        onSortedChange={(sorted) => {
          this.handleTableStateChange({
            [ReactTablePropNameEnum.sorted]: sorted,
          });
          onSortedChange(sorted);
        }}
        onPageSizeChange={(pageSize, page) => {
          this.handleTableStateChange({
            [ReactTablePropNameEnum.pageSize]: pageSize,
            [ReactTablePropNameEnum.page]: page,
          });
          onPageSizeChange(pageSize, page);
        }}
        onPageChange={(page) => {
          this.handleTableStateChange({ [ReactTablePropNameEnum.page]: page });
          onPageChange(page);
        }}
        getTheadThProps={
          draggableColumns ? this.draggableHeader : getTheadThProps
        }
        innerRef={(ref) => {
          this.innerTableRef.current = ref;
          if (reactTableRef) {
            reactTableRef.current = ref;
          }
        }}
        data={tableData}
        manual={manual}
        {...rest}
      />
    );
  }
}

const mapStateToProps = (store) => {
  const { gridPreference } = store.CommonState;
  return {
    allTableStates: getTableStates(store),
    gridPreferenceData: gridPreference.preferenceJson,
    gridPreferenceSubmitError: gridPreference.submitError,
    preferenceJsonError: gridPreference.preferenceJsonError,
    gridPreferenceSubmitted: gridPreference.submitted,
  };
};

const mapDispatchToProps = {
  setTableStates,
  getColumnOrderSchema,
  saveColumnOrderSchema,
  clearGridPreference,
  clearGridPreferenceSubmitError,
};

const ReactTableWrapper = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ReactTableContainer)
);

export default ReactTableWrapper;
export { ReactTableWrapper };



// WEBPACK FOOTER //
// ./src/common/Components/ReactTableWrapper/ReactTableWrapper.js